import {
  consumeBlurredCell,
  detectFocusSkip,
  recordBlurredCell,
  shiftTabDebounce,
} from '@experiences/components/table/blocs/hooks/use-focus-skip-detection';
import {
  getFocusedColumnId,
  useTableFocusStore,
} from '@experiences/components/table/blocs/stores/table-focus-store';
import {
  handleWebKeyPress,
  hasSystemModifierKey,
  isShiftPressed,
  Key,
} from '@platform/shortcuts/utils/keyboard-platform';
import {
  NativeSyntheticEvent,
  Platform,
  TextInputKeyPressEventData,
  TextInput as TextInputType,
} from 'react-native';
import { useCallback, useEffect, useRef, useTransition } from 'react';
import { useClearFocusAndBlur } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-clear-focus-and-blur';
import { useRafThrottle } from '@experiences/hooks/use-raf-throttle';
import { useTableContext } from '@experiences/components/table/blocs/table-context';

interface StoreActions {
  moveFocusDown: (tableId: string) => number | null;
  moveFocusLeft: (tableId: string) => unknown;
  moveFocusRight: (tableId: string) => unknown;
  moveFocusUp: (tableId: string) => number | null;
  setFocusedColumn: (tableId: string, columnId: string | null) => void;
  setFocusedIndex: (tableId: string, index: number | null) => void;
}

interface ExtraKeyPressHelpers {
  blur: () => void;
  clearFocus: () => void;
  getStoreActions: () => StoreActions;
  moveFocus: (moveFn: (tableId: string) => unknown) => void;
}

interface UseCellKeyboardNavigationOptions {
  /** The column this cell represents. */
  columnId: string;
  /**
   * Whether this is the default column (claims focus when focusedColumn is null).
   * Only true for the Quantity column.
   */
  isDefaultColumn?: boolean;
  /**
   * Optional extra key handler called after Escape but before Tab/Arrow handlers.
   * @returns true if the event was handled (prevents default Tab/Arrow handlers from running).
   */
  onExtraKeyPress?: (
    e: NativeSyntheticEvent<TextInputKeyPressEventData>,
    helpers: ExtraKeyPressHelpers,
  ) => boolean;
  /**
   * Optional callback invoked on focus, after updating the store.
   * Used by Quantity for iOS text selection.
   */
  onFocusExtra?: () => void;
  /**
   * Optional pre-handler invoked after system-modifier and Escape checks but
   * before Tab/Arrow navigation. Return `true` to indicate the event was
   * handled (prevents further processing).
   *
   * Use this to compose domain-specific key handlers (e.g. order-entry
   * shortcuts from {@link useOrderEntryCellShortcuts}).
   */
  onPreKeyPress?: (
    e: NativeSyntheticEvent<TextInputKeyPressEventData>,
    clearFocusAndBlur: () => void,
  ) => boolean;
  /** Rendered row index (sorted position). */
  rowIndex: number;
}

interface UseCellKeyboardNavigationResult {
  /** Clears focus store state if this cell is still the focused one. Call at the start of handleBlur. */
  handleBlurFocusCleanup: () => void;
  handleFocus: () => void;
  handleKeyPress: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
  inputRef: React.RefObject<TextInputType | null>;
  isCellFocused: boolean;
  moveFocus: (moveFn: (tableId: string) => unknown) => void;
}

export const useCellKeyboardNavigation = ({
  columnId,
  isDefaultColumn,
  onExtraKeyPress,
  onFocusExtra,
  onPreKeyPress,
  rowIndex,
}: UseCellKeyboardNavigationOptions): UseCellKeyboardNavigationResult => {
  const inputRef = useRef<TextInputType>(null);
  const throttle = useRafThrottle();
  const [, startTransition] = useTransition();

  const {
    isActive: isTableActive,
    nativeFocusSkipRef,
    onFocusedIndexChange,
    scrollToColumn,
    tableId,
  } = useTableContext();

  const isCellFocused = useTableFocusStore((state) => {
    if (state.suppressCellFocus) {
      return false;
    }
    if (state.focusedIndexByTableId[tableId] !== rowIndex) {
      return false;
    }
    const focused = getFocusedColumnId(state.columnsByTableId[tableId]);
    return (
      focused === columnId || (isDefaultColumn === true && focused === null)
    );
  });

  // PERF: Use getState() for action functions instead of selectors.
  // These functions are stable and don't need reactivity - using selectors
  // creates unnecessary subscriptions that fire on every store update.
  const getStoreActions = useCallback((): StoreActions => {
    const store = useTableFocusStore.getState();
    return {
      moveFocusDown: store.moveFocusDown,
      moveFocusLeft: store.moveFocusLeft,
      moveFocusRight: store.moveFocusRight,
      moveFocusUp: store.moveFocusUp,
      setFocusedColumn: store.setFocusedColumn,
      setFocusedIndex: store.setFocusedIndex,
    };
  }, []);

  // Auto-focus when this cell becomes keyboard-focused via navigation.
  useEffect(() => {
    if (!isCellFocused || isTableActive === false) {
      return;
    }
    inputRef.current?.focus();
  }, [isTableActive, isCellFocused]);

  const handleFocus = () => {
    if (!isTableActive) {
      return;
    }

    // Detect and correct iPad Shift+Tab column skips (native only).
    const store = useTableFocusStore.getState();
    const cols = store.columnsByTableId[tableId];
    const redirect =
      Platform.OS !== 'web' && cols
        ? detectFocusSkip({
            blurred: consumeBlurredCell(nativeFocusSkipRef),
            editable: cols.editable,
            newColumnId: columnId,
            newRowIndex: rowIndex,
            rowCount: store.totalRowsByTableId[tableId] ?? 0,
          })
        : null;

    const { setFocusedColumn, setFocusedIndex } = getStoreActions();

    if (redirect) {
      setFocusedIndex(tableId, redirect.rowIndex);
      setFocusedColumn(tableId, redirect.columnId);
      scrollToColumn?.(redirect.columnId);
      return;
    }

    setFocusedIndex(tableId, rowIndex);
    setFocusedColumn(tableId, columnId);
    scrollToColumn?.(columnId);
    onFocusExtra?.();
  };

  const moveFocus = (moveFn: (tableId: string) => unknown) => {
    throttle(() => {
      startTransition(() => {
        const result = moveFn(tableId);
        const newIndex =
          typeof result === 'number'
            ? result
            : ((result as { row?: number | null } | null)?.row ?? null);
        onFocusedIndexChange?.(newIndex);
      });
    });
  };

  const clearFocusAndBlur = useClearFocusAndBlur(inputRef);

  const clearFocus = () => {
    const { setFocusedColumn, setFocusedIndex } = getStoreActions();
    startTransition(() => {
      setFocusedIndex(tableId, null);
      setFocusedColumn(tableId, null);
    });
  };

  const handleKeyPress = (
    e: NativeSyntheticEvent<TextInputKeyPressEventData>,
  ) => {
    if (!isTableActive) {
      return;
    }
    handleWebKeyPress(e);

    if (hasSystemModifierKey(e)) {
      return;
    }

    const key = e.nativeEvent.key;

    if (key === Key.Escape) {
      clearFocusAndBlur();
      return;
    }

    if (onPreKeyPress?.(e, clearFocusAndBlur)) {
      return;
    }

    // Allow cells to handle extra keys (e.g. Quantity's row-0 edge cases)
    if (
      onExtraKeyPress?.(e, {
        blur: () => inputRef.current?.blur(),
        clearFocus,
        getStoreActions,
        moveFocus,
      })
    ) {
      return;
    }

    const isTab = key === Key.Tab;
    const isShiftTab = isTab && isShiftPressed(e);

    if (isTab && !isShiftTab) {
      e.preventDefault?.();
      moveFocus(getStoreActions().moveFocusRight);
      return;
    }

    if (isShiftTab) {
      e.preventDefault?.();
      //  debounce shift+tab input on iOS native behavior of focusing the search input if we are not there yet
      if (Platform.OS === 'ios') {
        shiftTabDebounce.trigger();
      }
      moveFocus(getStoreActions().moveFocusLeft);
      return;
    }

    if (key === Key.ArrowUp) {
      moveFocus(getStoreActions().moveFocusUp);
      return;
    }

    if (key === Key.ArrowDown) {
      moveFocus(getStoreActions().moveFocusDown);
      return;
    }
  };

  const handleBlurFocusCleanup = () => {
    const store = useTableFocusStore.getState();
    if (store.focusedIndexByTableId[tableId] !== rowIndex) {
      return;
    }
    const focused = getFocusedColumnId(store.columnsByTableId[tableId]);
    const isThisCellFocused =
      focused === columnId || (isDefaultColumn === true && focused === null);
    if (isThisCellFocused) {
      // Breadcrumb for iPad Shift+Tab skip detection (native only).
      if (Platform.OS !== 'web') {
        recordBlurredCell(nativeFocusSkipRef, { columnId, rowIndex });
      }
      const { setFocusedColumn, setFocusedIndex } = getStoreActions();
      setFocusedIndex(tableId, null);
      setFocusedColumn(tableId, null);
    }
  };

  return {
    handleBlurFocusCleanup,
    handleFocus,
    handleKeyPress,
    inputRef,
    isCellFocused,
    moveFocus,
  };
};
